import {
  Box,
  Button,
  Flex,
  type FlexProps,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Text,
  Wrap,
  WrapItem,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  type SafeSendOperation,
  type Transaction,
  formatPkh,
  operationSign,
  prettyTezAmount,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useAppSelector, useSelectedNetwork } from "@umami/state";
import capitalize from "lodash/capitalize";

import { Timestamp } from "./Timestamp";
import { TransactionDirectionIcon } from "./TransactionDirectionIcon";
import { useFee } from "./useFee";
import { useOperationColor } from "./useOperationColor";
import { ContactIcon, HourglassIcon, SafeSendIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";

import { ShareSecretKeyBox } from "@/components/SafeSend/ShareSecretKeyBox";
import { SignPage as ClaimSignPage } from "@/components/SendFlow/ClaimSafeSend/SignPage";
import { SignPage as WithdrawSignPage } from "@/components/SendFlow/WithdrawSafeSend/SignPage";

export const SafeSendTxTile = ({
  operation,
  ...props
}: { operation: SafeSendOperation } & FlexProps) => {
  const color = useColor();
  const fee = useFee(operation);
  const amount = prettyTezAmount(operation.amount);
  const { openWith } = useDynamicModalContext();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const isWithdraw = operation.action === "send";
  const isClaimable = operation.action === "claim";
  const isOpen = operation.safeSendStatus === "open";
  const destination = isWithdraw ? "outgoing" : "incoming";
  const titleColor = useOperationColor(destination);
  const sign = operationSign(destination);

  const handleClick = () => {
    if (!isOpen) {
      return;
    }

    return openWith(
      isWithdraw ? (
        <WithdrawSignPage operation={operation as unknown as Transaction} />
      ) : (
        <ClaimSignPage operation={operation as unknown as Transaction} />
      )
    );
  };
  const withdrawButton = (isWithdraw || isClaimable) && (
    <Button
      width={isMobile ? "100%" : "auto"}
      height="40px"
      padding="12px 24px"
      color={isOpen ? "white" : color("400")}
      background={isOpen ? color("900") : color("200")}
      border={isOpen ? "none" : `1px solid ${color("400")}`}
      onClick={handleClick}
    >
      {isOpen ? (isWithdraw ? "Withdraw" : "Claim") : capitalize(operation.safeSendStatus)}
    </Button>
  );

  return (
    <Flex flexDirection="column" gap="20px" width="100%" {...props}>
      {isMobile ? (
        <Flex flexDirection="column" gap="12px" width="100%">
          <AmountSection
            amount={amount}
            destination={destination}
            fee={fee}
            sign={sign}
            titleColor={titleColor}
          />

          <TransactionDetails operation={operation} />

          <SafeSendInfo
            hash={operation.parameter.value?.hash_ || operation.parameter.value?.hash}
            isWithdraw={isWithdraw}
          />
          {withdrawButton}
        </Flex>
      ) : (
        <Flex flexDirection="column" gap="20px" width="100%">
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Flex flexDirection="column" gap="8px">
              <AmountSection
                amount={amount}
                destination={destination}
                fee={fee}
                sign={sign}
                titleColor={titleColor}
              />
              <SafeSendInfo
                hash={operation.parameter.value?.hash_ || operation.parameter.value?.hash}
                isWithdraw={isWithdraw}
              />
            </Flex>
            {withdrawButton}
          </Flex>

          <TransactionDetails operation={operation} />
        </Flex>
      )}
    </Flex>
  );
};

const AmountSection = ({
  amount,
  destination,
  fee,
  sign,
  titleColor,
}: {
  amount: string;
  destination: "incoming" | "outgoing";
  fee: number | string | null;
  sign: string;
  titleColor: string;
}) => {
  const color = useColor();

  return (
    <Wrap spacing="10px">
      <WrapItem>
        <TransactionDirectionIcon marginRight="8px" destination={destination} />
        <Text color={titleColor} fontSize="16px" fontWeight={600} lineHeight="22px">
          {sign} {amount}
        </Text>
      </WrapItem>
      {fee && (
        <WrapItem>
          <Flex gap="4px" color={color("600")} lineHeight="22px">
            <Text size="md">Fee:</Text>
            <Text data-testid="fee">{prettyTezAmount(fee)}</Text>
          </Flex>
        </WrapItem>
      )}
    </Wrap>
  );
};

const SafeSendInfo = ({ hash, isWithdraw }: { hash: string; isWithdraw: boolean }) => {
  const color = useColor();
  const secrets = useAppSelector(state => state.safeSendSecrets.secrets);
  const { openWith } = useDynamicModalContext();

  return (
    <Flex
      alignItems="center"
      justifyContent="flex-start"
      gap="8px"
      width={isWithdraw ? "auto" : "fit-content"}
      padding={isWithdraw ? "4px 4px 4px 8px" : "4px 8px"}
      background={color("100")}
      borderRadius="80px"
    >
      <Box width="21.66px" height="20px">
        <SafeSendIcon width="21.66px" height="20px" color={color("400")} />
      </Box>
      <Text color={color("600")} fontSize="16px" fontWeight={600} lineHeight="22px">
        Safe send transaction
      </Text>
      {isWithdraw && (
        <Box padding="6px 10px" background={color("100")} borderRadius="100px">
          {secrets[hash] ? (
            <Text
              color="blue.500"
              fontSize="14px"
              fontWeight={700}
              lineHeight="18px"
              cursor="pointer"
              onClick={() =>
                openWith(
                  <ModalContent>
                    <ModalCloseButton />
                    <ModalBody>
                      <ShareSecretKeyBox safeSendHash={hash} secret={secrets[hash]} />
                    </ModalBody>
                  </ModalContent>
                )
              }
            >
              Reveal Key
            </Text>
          ) : null}
        </Box>
      )}
    </Flex>
  );
};

const TransactionDetails = ({ operation }: { operation: SafeSendOperation }) => {
  const color = useColor();
  const network = useSelectedNetwork();

  return (
    <Wrap justify="space-between" spacingX="10px" spacingY="20px">
      <WrapItem>
        <Wrap spacing="10px">
          {operation.target && (
            <WrapItem data-testid="to">
              <Text marginRight="8px" color={color("600")}>
                To:
              </Text>
              <Flex padding="6px 10px" background={color("100")} borderRadius="100px">
                <Flex alignItems="flex-start" justifyContent="flex-start" gap="4px">
                  <Box position="relative" overflow="hidden" width="18px" height="18px">
                    <ContactIcon width="18px" height="18px" color={color("400")} />
                  </Box>
                  <Text color={color("700")} fontSize="14px" fontWeight="400" lineHeight="18px">
                    {formatPkh(operation.target.address)}
                  </Text>
                </Flex>
              </Flex>
            </WrapItem>
          )}
        </Wrap>
      </WrapItem>

      <WrapItem alignItems="center">
        <Flex alignItems="center" gap="10px">
          <Timestamp timestamp={operation.timestamp} />
          <Flex alignItems="center" gap="4px">
            <Link
              color={color("600")}
              _hover={{ textDecoration: "none" }}
              data-testid="operation-type"
              href={network.explorerUrl + "/" + operation.hash}
              isExternal
              size="md"
            >
              Transaction
            </Link>
            <Box position="relative" overflow="hidden" width="18px" height="18px">
              {/* TODO this needs to be updated once the transaction is claimed. Use status. */}
              <HourglassIcon width="18px" height="18px" color={color("400")} />
            </Box>
          </Flex>
        </Flex>
      </WrapItem>
    </Wrap>
  );
};
